import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useAuths, useCreateAuth, useUpdateAuth, useDeleteAuth } from "@/hooks/use-auths";
import type { Auth } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const defaultAuth = {
  name: "",
  auth_type: "userpass",
  username: "",
  password: "",
  password_digest: null as string | null,
};

const columns: Column<Auth>[] = [
  { key: "name", header: "Name" },
  { key: "auth_type", header: "Type", render: (a) => <span className="font-mono text-xs">{a.auth_type}</span> },
  { key: "username", header: "Username" },
];

export default function AuthsPage() {
  const { data, isLoading } = useAuths();
  const createMut = useCreateAuth();
  const updateMut = useUpdateAuth();
  const deleteMut = useDeleteAuth();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Auth | null>(null);
  const [form, setForm] = useState(defaultAuth);

  const openCreate = () => { setEditing(null); setForm(defaultAuth); setOpen(true); };
  const openEdit = (a: Auth) => {
    setEditing(a);
    const { id, created_at, updated_at, ...rest } = a;
    setForm({ ...rest });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, {
        onSuccess: () => { setOpen(false); toast.success("Auth updated"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    } else {
      createMut.mutate(form as typeof form, {
        onSuccess: () => { setOpen(false); toast.success("Auth created"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    }
  };

  const handleDelete = (a: Auth) => {
    deleteMut.mutate(a.id, {
      onSuccess: () => toast.success("Auth deleted"),
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Auths</h1>
          <p className="text-sm text-muted-foreground mt-1">PJSIP authentication credentials</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create Auth</Button>
      </div>

      <DataTable columns={columns} data={data} isLoading={isLoading} getRowKey={(a) => a.id} onEdit={openEdit} onDelete={handleDelete} emptyMessage="No auths configured." />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit Auth" : "Create Auth"}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name * <HelpIcon text="Unique auth identifier. Referenced by endpoints as auth or outbound_auth. Example: trunk-auth, user-1001." /></Label>
              <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="auth_type">Type <HelpIcon text="Authentication mechanism. userpass - username/password MD5 digest auth. md5 - pre-computed MD5 digest." /></Label>
              <Select id="auth_type" value={form.auth_type} onChange={(e) => updateField("auth_type", e.target.value)}>
                <option value="userpass">User/Pass</option>
                <option value="md5">MD5</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username * <HelpIcon text="SIP authentication username. For inbound auth: what Asterisk challenges callers for. For outbound auth: what Asterisk sends to the provider. Example: mytrunkuser." /></Label>
              <Input id="username" value={form.username} onChange={(e) => updateField("username", e.target.value)} />
            </div>
            {form.auth_type === "userpass" ? (
              <div className="space-y-1.5">
                <Label htmlFor="password">Password * <HelpIcon text="SIP authentication password/secret. Used with username for MD5 digest calculation during SIP 401/407 challenges." /></Label>
                <Input id="password" type="password" value={form.password} onChange={(e) => updateField("password", e.target.value)} />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="password_digest">Password Digest * <HelpIcon text='Pre-computed MD5 digest. Generate with: echo -n "myuser:somedomain:somepassword" | openssl dgst -md5' /></Label>
                <Input id="password_digest" value={form.password_digest ?? ""} onChange={(e) => updateField("password_digest", e.target.value || null)} />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}